import { call, put, takeEvery } from "redux-saga/effects";
import {
  addOemRequest,
  addOemSuccess,
  addOemFailure,
  getOemRequest,
  getOemSuccess,
  getOemFailure,
  getScanTypesSuccess,
  getScanTypesFailure,
  getOemNamesSuccess,
  getOemNamesFailure,
  getLicensesSuccess,
  getLicensesFailure,
  getLicensesListSuccess,
  getLicensesListFailure,
  getOemPartnerUsersSuccess,
  getOemPartnerUsersFailure,
} from "../actions/oemActions";
import { toast } from "react-toastify";
import { oemApi } from "../api/oemApi";

function* addOemSaga(action) {
  try {
    yield put(addOemRequest());
    const response = yield call(oemApi.addPartner, action.payload);
    yield put(addOemSuccess(response.data));
    toast.success("OEM Partner created successfully!");
    const refreshParams = action.payload.refreshParams || {
      limit: 10,
      page: 1,
    };
    yield put({ type: "GET_OEM_SAGA", payload: refreshParams });
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to add OEM partner";
    yield put(addOemFailure(errorMessage));
    toast.error(errorMessage);
  }
}

function* getOemSaga(action) {
  try {
    yield put(getOemRequest());
    const response = yield call(oemApi.getPartners, action.payload);
    yield put(getOemSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch OEM partners";
    yield put(getOemFailure(errorMessage));
  }
}

function* editOemSaga(action) {
  try {
    yield put({ type: "EDIT_OEM_REQUEST" });
    const response = yield call(oemApi.editPartner, action.payload);
    yield put({ type: "EDIT_OEM_SUCCESS", payload: response.data });
    toast.success("OEM Partner updated successfully!");
    const refreshParams = action.payload.refreshParams || {
      limit: 10,
      page: 1,
    };
    yield put({ type: "GET_OEM_SAGA", payload: refreshParams });
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to update OEM partner";
    yield put({ type: "EDIT_OEM_FAILURE", payload: errorMessage });
    toast.error(errorMessage);
  }
}

function* deleteOemSaga(action) {
  try {
    yield put({ type: "DELETE_OEM_REQUEST" });
    const response = yield call(
      oemApi.deletePartner,
      action.payload.id || action.payload,
    );
    yield put({
      type: "DELETE_OEM_SUCCESS",
      payload: action.payload.id || action.payload,
    });
    toast.success("OEM Partner deleted successfully!");
    if (response.status === 200) {
      const refreshParams = action.payload.refreshParams || {
        limit: 10,
        page: 1,
      };
      yield put({ type: "GET_OEM_SAGA", payload: refreshParams });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete OEM partner";
    yield put({ type: "DELETE_OEM_FAILURE", payload: errorMessage });
    toast.error(errorMessage);
  }
}

function* changeOemStatusSaga(action) {
  try {
    yield put({ type: "CHANGE_OEM_STATUS_REQUEST" });
    const response = yield call(oemApi.changeStatus, action.payload);
    yield put({ type: "CHANGE_OEM_STATUS_SUCCESS", payload: response.data });
    toast.success(response.data?.message || "Status updated successfully!");
    // Preserve current filter parameters when refreshing
    const refreshParams = action.payload.refreshParams || {
      limit: 10,
      page: 1,
    };
    yield put({ type: "GET_OEM_SAGA", payload: refreshParams });
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to update status";
    yield put({ type: "CHANGE_OEM_STATUS_FAILURE", payload: errorMessage });
    toast.error(errorMessage);
  }
}

function* getScanTypesSaga() {
  try {
    yield put({ type: "GET_SCAN_TYPES_REQUEST" });
    const response = yield call(oemApi.getScanTypes);
    yield put(getScanTypesSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch scan types";
    yield put(getScanTypesFailure(errorMessage));
  }
}

function* getOemNamesSaga() {
  try {
    yield put({ type: "GET_OEM_NAMES_REQUEST" });
    const response = yield call(oemApi.getOemNames);
    yield put(getOemNamesSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch OEM names";
    yield put(getOemNamesFailure(errorMessage));
  }
}

function* resetOemPasswordSaga(action) {
  try {
    yield put({ type: "RESET_OEM_PASSWORD_REQUEST" });
    const response = yield call(oemApi.resetPassword, action.payload);
    yield put({ type: "RESET_OEM_PASSWORD_SUCCESS", payload: response });
    toast.success(
      response.data?.message || "Password reset successfully and email sent!",
    );

    // Refresh the partners list after successful password reset
    const refreshParams = action.payload.refreshParams || {
      limit: 10,
      page: 1,
    };
    yield call(oemApi.getPartners, refreshParams);
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to reset password";
    yield put({ type: "RESET_OEM_PASSWORD_FAILURE", payload: errorMessage });
    toast.error(errorMessage);
  }
}

function* getLicensesSaga(action) {
  try {
    yield put({ type: "GET_LICENSES_REQUEST" });
    const response = yield call(oemApi.getLicenses, action.payload);
    yield put(getLicensesSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch licenses";
    yield put(getLicensesFailure(errorMessage));
  }
}

function* getLicensesListSaga(action) {
  try {
    yield put({ type: "GET_LICENSES_LIST_REQUEST" });
    const response = yield call(oemApi.getLicensesList, action.payload);
    yield put(getLicensesListSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch licenses list";
    yield put(getLicensesListFailure(errorMessage));
  }
}

function* getOemPartnerUsersSaga(action) {
  try {
    yield put({ type: "GET_OEM_PARTNER_USERS_REQUEST" });
    const response = yield call(oemApi.getOemPartnerUsers, action.payload);
    yield put(getOemPartnerUsersSuccess(response));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch OEM partner users";
    yield put(getOemPartnerUsersFailure(errorMessage));
  }
}

function* oemSaga() {
  yield takeEvery("ADD_OEM_SAGA", addOemSaga);
  yield takeEvery("GET_OEM_SAGA", getOemSaga);
  yield takeEvery("EDIT_OEM_SAGA", editOemSaga);
  yield takeEvery("DELETE_OEM_SAGA", deleteOemSaga);
  yield takeEvery("CHANGE_OEM_STATUS_SAGA", changeOemStatusSaga);
  yield takeEvery("RESET_OEM_PASSWORD_SAGA", resetOemPasswordSaga);
  yield takeEvery("GET_SCAN_TYPES_SAGA", getScanTypesSaga);
  yield takeEvery("GET_OEM_NAMES_SAGA", getOemNamesSaga);
  yield takeEvery("GET_LICENSES_SAGA", getLicensesSaga);
  yield takeEvery("GET_LICENSES_LIST_SAGA", getLicensesListSaga);
  yield takeEvery("GET_OEM_PARTNER_USERS_SAGA", getOemPartnerUsersSaga);
}

export default oemSaga;
